import {
  type CustomFieldValueBulkInput,
  EnumCustomFieldDefinitionFieldType,
  EnumCustomFieldValueRecordType,
} from '../../lib/graphql';

import { type CustomFieldDef } from './CustomFieldsRenderer';

/**
 * Convert a form value into the typed slot expected by `customFieldValueBulkSave`.
 *
 * Returns `null` for empty values (undefined, null, empty string) so callers
 * can `.filter(Boolean)` the result list and skip writing no-op rows. A `false`
 * boolean and a `0` number are NOT empty — both are preserved.
 *
 * The `'yes'`/`'no'` string legacy path covers Toggle fields whose form state
 * came from an older form widget that stored string flags. Both flow paths
 * (head-edit, status-change-killed-modal) historically did this; centralizing
 * here keeps the registry's typed-slot dispatch in one place.
 * @param def
 * @param def._id
 * @param def.fieldType
 * @param recordId
 * @param recordType
 * @param value
 */
export function serializeCustomFieldInput(
  def: { _id: string; fieldType?: CustomFieldDef['fieldType'] },
  recordId: string,
  recordType: EnumCustomFieldValueRecordType,
  value: unknown,
): CustomFieldValueBulkInput | null {
  if (value === undefined || value === null || value === '') return null;

  const base = {
    definitionId: def._id,
    recordId,
    recordType,
  };

  if (Array.isArray(value)) {
    return { ...base, value_json: value };
  }
  if (typeof value === 'boolean') {
    return { ...base, value_boolean: value };
  }
  if (value === 'yes' || value === 'no') {
    return { ...base, value_boolean: value === 'yes' };
  }
  if (typeof value === 'number') {
    return { ...base, value_number: value };
  }
  if (
    def.fieldType === EnumCustomFieldDefinitionFieldType.Number &&
    typeof value === 'string' &&
    !Number.isNaN(Number(value))
  ) {
    return { ...base, value_number: Number(value) };
  }
  return { ...base, value_text: String(value) };
}
